import React, { useState, useMemo, lazy, Suspense } from 'react';
import Checkbox from '@mui/material/Checkbox';
import {
  Typography,
  Button,
  MenuItem,
  ListItemText,
  TextField,
  CardContent,
  Grid,
  Box,
} from '@mui/material';
import MuiSelect from '@mui/material/Select';
import shadows from '@mui/material/styles/shadows';
import { View } from './downloadCenterStyles';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Form, Field, Formik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { useAuth } from '../../services/contexts/AuthContext';
import api from '../../Middleware/api/api';
import LogoLoader from '../../components/LogoLoader';
import { getUserTypeData } from '../../services/utils/translations-constants.js';
import {
  formatCpoIdWithName,
  translatePaymentMethod,
  formatLocationIdWithName,
  DownloadCenterFieldRow,
} from './downloadCenterUtils';

function calculatedates(days) {
  let date = new Date();
  let last = new Date(date.getTime() - days * 24 * 60 * 60 * 1000);
  return (
    last.getFullYear() +
    '-' +
    last.toLocaleString('en-US', { month: '2-digit' }) +
    '-' +
    last.toLocaleString('en-US', { day: '2-digit' })
  );
}
const FailFeedback = lazy(() => import('../../components/DataTable/Feedback'));
function DownloadErpDataBills({ stations, operators }) {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [selectedCpo, setSelectedCpo] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState('SEPA');
  const [error, setError] = useState(false);
  const [error204, setError204] = useState(false);
  const [error404, setError404] = useState(false);
  const [startDate, setStartDate] = useState(calculatedates(7));
  const [endDate, setEndDate] = useState(calculatedates(0));
  const paymentMethodsTypes = [
    { value: 'SEPA', translationKey: 'LBLsepamode' },
    { value: 'BANK_TRANSFER', translationKey: 'rechnung' },
  ];
  const [ButtonDownloadErpIsSubmitting, setButtonDownloadErpIsSubmitting] =
    useState(false);

  const [xmlErrorMessage, setXmlErrorMessage] = useState('');
  const [xmlError, setXmlError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stationsWithCpo, setStationsWithCpo] = useState([]);
  const initialValues = {
    startDate: startDate,
    endDate: endDate,
  };
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const { userTypeMap } = getUserTypeData(t);
  const userTypeList = ['REGULAR', 'LOCAL_ADMIN'];
  const isAllUserTypesSelected =
    userTypeList?.length > 0 && selectedUserTypes?.length > 0;
  selectedUserTypes?.length === userTypeList?.length;

  const isAllSelectedStations =
    stationsWithCpo?.length > 0 &&
    selectedLocations?.length === stationsWithCpo?.length;
  const exportType = 'XML';
  const validationSchema = useMemo(
    () =>
      Yup.object({
        startDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .required(t('LBLReqCockpit')),
        endDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .min(Yup.ref('startDate'), t('LBLENdgStart'))
          .required(t('LBLEndDateReq')),
      }),
    []
  );

  const getFileName = (response) => {
    const contentDisposition = response?.headers['content-disposition'];
    if (contentDisposition && contentDisposition.includes('filename=')) {
      return contentDisposition.split('filename=')[1].replace(/['"]/g, '');
    }
    return 'ERPDaten_' + format(new Date(), 'yyyyMMdd') + '.xml';
  };

  const exportErpDataBills = (startDateValue, endDateValue) => {
    const urlParams = new URLSearchParams();
    urlParams.set('startDate', startDateValue);
    urlParams.set('endDate', endDateValue);

    urlParams.set('cpoId', selectedCpo.toString());
    urlParams.set('userTypes', selectedUserTypes?.toString());
    if (selectedLocations?.length > 0) {
      urlParams.set('locationIds', selectedLocations?.toString());
    }
    urlParams.set('paymentMethod', paymentMethodSelected);

    api
      .exportErpDataBills(token, urlParams)
      .then((response) => {
        if (response?.status == 204) {
          setError204(true);
        } else if (response?.status == 404) {
          setError404(true);
        } else if (response?.status == 200) {
          if (response?.data) {
            let filename = getFileName(response);
            const url = window.URL.createObjectURL(new Blob([response?.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        } else {
          setError(true);
        }
        setButtonDownloadErpIsSubmitting(false);
        setLoading(false);
      })
      .catch((error) => {
        const errorCodeResponse =
          error?.response?.data?.error?.errorCodeResponse;
        setButtonDownloadErpIsSubmitting(false);
        setLoading(false);
        if (errorCodeResponse === 'BILLING_NOT_FOUND') {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadNoBillsFound'));
        } else if (errorCodeResponse === 'CONTRACT_NOT_FOUND') {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadNoContractsFound'));
        } else if (errorCodeResponse === 'OPERATOR_NOT_FOUND') {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadNoOperatorFound'));
        } else if (errorCodeResponse === 'ERP_VALUE_NOT_FOUND') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoERPValuefound') + ' ' + errorObj
          );
        } else if (errorCodeResponse === 'ERP_SYSTEM_PARAMETERS_NOT_FOUND') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoErpSystemParametersForContract') +
              ' ' +
              errorObj
          );
        } else if (errorCodeResponse === 'DEBIT_ACCOUNT_NR_MISSING') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoDebitAccountNrForContract', {
              endCustomerId: errorObj,
            })
          );
        } else if (errorCodeResponse === 'COST_CENTER_NR_MISSING') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoCostCenterNumberForContract', {
              endCustomerId: errorObj,
            })
          );
        } else if (errorCodeResponse === 'VAT_ID_MISSING') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlNoVatId', { endCustomerId: errorObj })
          );
        } else if (errorCodeResponse === 'CLIENT_NUMBER_MISSING') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoClientNumberForErpForOperator') +
              ' ' +
              errorObj
          );
        } else if (errorCodeResponse === 'NET_LEDGER_ACCOUNT_NUMBER_MISSING') {
          let errorObj = error?.response?.data?.error?.errorDetail;
          setXmlError(true);
          setXmlErrorMessage(
            t('LBLErpXmlDownloadNoNetLedgerAccountNrForErpForOperator') +
              ' ' +
              errorObj
          );
        } else if (errorCodeResponse === 'XML_GENERATION_ERROR') {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadUnkNownError1'));
        } else if (errorCodeResponse === 'UNKNOWN_ERROR') {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadUnkNownError1'));
        } else {
          setXmlError(true);
          setXmlErrorMessage(t('LBLErpXmlDownloadUnkNownError2'));
        }
      });
  };

  const closeErrorFeedback = () => setXmlError(false);

  const handleFromDatePostingChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setStartDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setStartDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setStartDate(newValue);
      setFieldValue('startDate', newValue);
      console.error('Invalid FromDatePosting input');
    }
  };

  const handleUntilDatePostingChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setEndDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setEndDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setEndDate(newValue);
      setFieldValue('endDate', newValue);
      console.error('Invalid UntilDatePosting input');
    }
  };

  const listofstations = (cpoId) => {
    return stations?.filter((element) => element?.cpoId === cpoId);
  };

  const handlePaymentMethodChange = (event) => {
    if (event?.target?.value !== undefined) {
      setPaymentMethodSelected(event?.target?.value);
    }
  };

  const handleTestCpoIdChange = (event) => {
    const newValue = event?.target?.value;
    if (event?.target?.value !== undefined && event?.target?.value !== 0) {
      setSelectedCpo(newValue);
      setStationsWithCpo([]);
      setSelectedLocations([]);
      setStationsWithCpo(listofstations(newValue));
    }
  };
  //fetch for ERP CSV file
  const handleSubmit = (values, actions) => {
    setStartDate(values?.startDate);
    setEndDate(values?.endDate);
    setButtonDownloadErpIsSubmitting(true);
    setLoading(true);
    exportErpDataBills(values?.startDate, values?.endDate);

    actions.setTouched({}, false);
    actions.setErrors({});
    actions.setFieldError({});
    actions.setFieldTouched({}, false, false);
    actions.setSubmitting(false);
  };

  const handleTestLocationChange = (event) => {
    if (event.target.value !== undefined) {
      if (
        event.target.value.includes('all') &&
        selectedLocations?.length !== stationsWithCpo?.length
      ) {
        setSelectedLocations(
          stationsWithCpo?.map((station) => station?.locationId)
        );
        setLocationSelectOpen(false);
      } else if (event.target.value.includes('all')) {
        setSelectedLocations([]);
      } else {
        setSelectedLocations(event.target.value);
      }
    }
  };

  const handleTestUserTypeChange = (event) => {
    if (event?.target?.value !== undefined) {
      if (
        event?.target?.value.includes('all') &&
        selectedUserTypes?.length !== userTypeList?.length
      ) {
        setSelectedUserTypes(userTypeList?.map((userType) => userType));
        setUserTypeSelectOpen(false);
      } else if (event?.target?.value?.includes('all')) {
        setSelectedUserTypes([]);
      } else {
        setSelectedUserTypes(event?.target?.value);
      }
      if (!event?.target?.value?.includes('REGULAR')) {
        setSelectedLocations([]);
      }
    }
  };
  const [userTypeSelectOpen, setUserTypeSelectOpen] = React.useState(false);
  const [locationSelectOpen, setLocationSelectOpen] = React.useState(false);

  return (
    <>
      <LogoLoader loading={loading} />
      <div>
        <div>
          <br></br>
          <Typography variant="h6" gutterBottom display="inline">
            {t('paraDownload2')}
          </Typography>
          <br></br>
          <br></br>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount
            onSubmit={handleSubmit}
          >
            {({ errors, values, setFieldValue }) => (
              <Form noValidate>
                <Grid container columnSpacing={2} rowSpacing={2}>
                  {/* LEFT COLUMN */}
                  <Grid item xs={12} md={6}>
                    <View>
                      <CardContent>
                        {/* CPO-ID */}
                        <DownloadCenterFieldRow
                          label={t('BTNbetreiberID') + ' *'}
                        >
                          <MuiSelect
                            disabled={stations?.length === 0}
                            multiple={false}
                            fullWidth
                            displayEmpty={false}
                            defaultValue={[]}
                            value={selectedCpo}
                            onChange={handleTestCpoIdChange}
                            renderValue={(selectedCpo) => {
                              return formatCpoIdWithName(
                                operators,
                                selectedCpo
                              );
                            }}
                            MenuProps={{
                              PaperProps: { sx: { boxShadow: shadows[3] } },
                            }}
                          >
                            {operators?.map((operator, idx) => (
                              <MenuItem key={idx} value={operator?.cpoId}>
                                {operator?.cpoId}: {operator?.cpoName}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </DownloadCenterFieldRow>

                        {/* UserType */}
                        <DownloadCenterFieldRow label={t('LBLUserType') + ' *'}>
                          <MuiSelect
                            multiple
                            fullWidth
                            displayEmpty={false}
                            defaultValue={[]}
                            value={selectedUserTypes}
                            onChange={handleTestUserTypeChange}
                            renderValue={(selected) =>
                              selected
                                ?.map((val) => userTypeMap[val])
                                .join(', ')
                            }
                            open={userTypeSelectOpen}
                            onOpen={() => {
                              setUserTypeSelectOpen(true);
                            }}
                            onClose={() => {
                              setUserTypeSelectOpen(false);
                            }}
                            MenuProps={{
                              PaperProps: { sx: { boxShadow: shadows[3] } },
                              onClose: (event, reason) => {
                                if (
                                  reason === 'backdropClick' ||
                                  reason === 'escapeKeyDown'
                                ) {
                                  setUserTypeSelectOpen(false);
                                } else {
                                  setUserTypeSelectOpen(true);
                                }
                              },
                            }}
                          >
                            <MenuItem
                              value="all"
                              divider
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isAllUserTypesSelected}
                                indeterminate={
                                  selectedUserTypes?.length > 0 &&
                                  selectedUserTypes?.length <
                                    userTypeList?.length
                                }
                              />
                              <ListItemText primary={t('LBLALLUserTypes')} />
                            </MenuItem>

                            {userTypeList?.map((usertype) => (
                              <MenuItem
                                key={usertype}
                                value={usertype}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedUserTypes?.includes(
                                    usertype
                                  )}
                                />
                                <ListItemText>
                                  {userTypeMap[usertype]}
                                </ListItemText>
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </DownloadCenterFieldRow>

                        {/* Locations */}
                        <DownloadCenterFieldRow
                          label={
                            t('MenuLocation') +
                            (selectedUserTypes?.includes('REGULAR') ? ' *' : '')
                          }
                        >
                          <MuiSelect
                            disabled={
                              stations?.length === 0 ||
                              selectedCpo === '' ||
                              !selectedUserTypes?.includes('REGULAR')
                            }
                            multiple={true}
                            fullWidth
                            displayEmpty={false}
                            defaultValue={[]}
                            value={selectedLocations}
                            onChange={handleTestLocationChange}
                            renderValue={(selected) =>
                              selected?.length > 0 && selected?.length < 6
                                ? selected?.join(', ')
                                : selected?.length + ' ' + t('LBLItemsSelected')
                            }
                            open={locationSelectOpen}
                            onOpen={() => setLocationSelectOpen(true)}
                            onClose={() => setLocationSelectOpen(false)}
                            MenuProps={{
                              PaperProps: { sx: { boxShadow: shadows[3] } },
                              onClose: (event, reason) => {
                                if (
                                  reason === 'backdropClick' ||
                                  reason === 'escapeKeyDown'
                                ) {
                                  setLocationSelectOpen(false);
                                } else {
                                  // item click or other reasons: keep it open
                                  setLocationSelectOpen(true);
                                }
                              },
                            }}
                          >
                            <MenuItem
                              value="all"
                              divider
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isAllSelectedStations}
                                indeterminate={
                                  selectedLocations?.length > 0 &&
                                  selectedLocations?.length <
                                    stationsWithCpo?.length
                                }
                              />
                              <ListItemText primary={t('LBLALLLocations')} />
                            </MenuItem>
                            {stationsWithCpo?.map((station) => (
                              <MenuItem
                                key={station?.locationId}
                                value={station?.locationId}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  size="small"
                                  checked={
                                    selectedLocations?.indexOf(
                                      station?.locationId
                                    ) > -1
                                  }
                                />
                                <ListItemText
                                  primaryTypographyProps={{ fontSize: '12px' }}
                                >
                                  {station?.locationId}: {station?.locationName}
                                </ListItemText>
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </DownloadCenterFieldRow>

                        {/* Payment Method */}
                        <DownloadCenterFieldRow
                          label={t('LBLpaymentMethod') + ' *'}
                        >
                          <TextField
                            select
                            fullWidth
                            id="paymentMethod"
                            name="PaymentMethod"
                            value={paymentMethodSelected}
                            onChange={handlePaymentMethodChange}
                          >
                            {paymentMethodsTypes?.map((method) => (
                              <MenuItem key={method.value} value={method.value}>
                                {t(method.translationKey)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </DownloadCenterFieldRow>

                        {/* Dates */}
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="de"
                        >
                          <DownloadCenterFieldRow
                            label={t('fromDatePosting') + ' *'}
                          >
                            <DatePicker
                              value={values.startDate}
                              onChange={(newValue) =>
                                handleFromDatePostingChange(
                                  newValue,
                                  setFieldValue
                                )
                              }
                              renderInput={(params) => (
                                <Field
                                  component={TextField}
                                  {...params}
                                  name="startDate"
                                  required
                                  fullWidth
                                  margin="none"
                                  autoComplete="off"
                                  helperText={errors.startDate}
                                  error={Boolean(errors.startDate)}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: 'TT.MM.JJJJ',
                                  }}
                                />
                              )}
                            />
                          </DownloadCenterFieldRow>

                          <DownloadCenterFieldRow
                            label={t('endPostingDate') + ' *'}
                          >
                            <DatePicker
                              value={values.endDate}
                              maxDate={dayjs()}
                              onChange={(newValue) =>
                                handleUntilDatePostingChange(
                                  newValue,
                                  setFieldValue
                                )
                              }
                              renderInput={(params) => (
                                <Field
                                  component={TextField}
                                  {...params}
                                  name="endDate"
                                  required
                                  fullWidth
                                  margin="none"
                                  autoComplete="off"
                                  helperText={errors.endDate}
                                  error={Boolean(errors.endDate)}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: 'TT.MM.JJJJ',
                                  }}
                                />
                              )}
                            />
                          </DownloadCenterFieldRow>
                        </LocalizationProvider>
                      </CardContent>
                    </View>
                  </Grid>

                  {/* RIGHT COLUMN */}
                  <Grid item xs={12} md={6}>
                    <View>
                      <CardContent>
                        <DownloadCenterFieldRow label={t('BTNbetreiberID')}>
                          <div>
                            {formatCpoIdWithName(operators, selectedCpo)}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('LBLUserType')}>
                          {selectedUserTypes?.length === 1 && (
                            <div>
                              {selectedUserTypes
                                ?.map((val) => userTypeMap[val])
                                .join(', ')}
                            </div>
                          )}
                          {selectedUserTypes?.length > 1 && (
                            <div>
                              {selectedUserTypes?.length}{' '}
                              {t('LBLItemsSelected')}
                            </div>
                          )}
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('MenuLocation')}>
                          {selectedLocations?.length > 0 &&
                            selectedLocations?.length < 6 && (
                              <div>
                                {formatLocationIdWithName(
                                  stationsWithCpo,
                                  selectedLocations
                                )}
                              </div>
                            )}
                          {selectedLocations?.length > 5 && (
                            <div>
                              {selectedLocations?.length}{' '}
                              {t('LBLItemsSelected')}
                            </div>
                          )}
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('LBLpaymentMethod')}>
                          <div>
                            {translatePaymentMethod(
                              t,
                              paymentMethodsTypes,
                              paymentMethodSelected
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('fromDatePosting')}>
                          <div>
                            {new Date(values.startDate).toLocaleDateString(
                              'de-DE'
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('endPostingDate')}>
                          <div>
                            {new Date(values.endDate).toLocaleDateString(
                              'de-DE'
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('Format')}>
                          <div>{exportType}</div>
                        </DownloadCenterFieldRow>

                        <Box
                          sx={{ textAlign: 'center', mt: { xs: 6, md: 12 } }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                              ButtonDownloadErpIsSubmitting ||
                              selectedCpo === '' ||
                              selectedUserTypes?.length === 0 ||
                              (selectedLocations?.length === 0 &&
                                selectedUserTypes?.includes('REGULAR')) ||
                              operators?.length === 0 ||
                              Boolean(errors?.startDate) ||
                              Boolean(errors?.endDate)
                            }
                          >
                            {t('BTNDownload')}
                          </Button>
                        </Box>
                      </CardContent>
                    </View>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <Suspense fallback={<div />}>
        <FailFeedback
          open={error}
          title={'Fehler'}
          severity="warning"
          message={
            <>
              <Typography align="center">
                {t('LBLSomethingwentWrong')}
              </Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
          }}
        />
        <FailFeedback
          open={error204}
          title={t('noinfoFound')}
          severity="warning"
          message={
            <>
              <Typography align="center">{t('LBLNoDataDC')}</Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
          }}
        />
        <FailFeedback
          open={error404}
          title={t('title404')}
          severity="warning"
          message={
            <>
              <Typography align="center">{t('body404')}</Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
            setError404(false);
          }}
        />
        <FailFeedback
          open={xmlError}
          title={t('LBLMissingInfo')}
          severity="warning"
          message={
            <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
              {xmlErrorMessage}
            </Typography>
          }
          handleClose={closeErrorFeedback}
        />
      </Suspense>
    </>
  );
}

export default DownloadErpDataBills;
